import { hasText } from 'src/utils';
import { getNewExpression, getOps, pushItem, getReturnType } from './utils';
import { ConditionOp, Expression, ExpressionPath, ExpressionFunction, LogOp, specsToTypes, typesMap, PathType, ExpressionFunctionSpec } from './types';
import { conf, getAvailAssoc, Field } from 'src/config';
import _ from 'lodash';


// ------------------------------------------------------------------------------------------------------------------
class Token {

    word: string | null = null;
    literal: string | null = null;
    expression: Expression | null = null;
    path: ExpressionPath | null = null;
    comma: boolean = false;

    static wordToken: (word: string) => Token = (word) => {
        const token = new Token();
        token.word = word;
        return token;
    }

    static literalToken: (literal: string) => Token = (literal) => {
        const token = new Token();
        token.literal = literal;
        return token;
    }

    static expressionToken: (expression: Expression) => Token = (expression) => {
        const token = new Token();
        token.expression = expression;
        return token;
    }

    static pathToken: (path: ExpressionPath) => Token = (path) => {
        const token = new Token();
        token.path = path;
        return token;
    }

    private constructor() { }

    public isSimpleWord: () => boolean = () => this.isWord() && this.word!.indexOf('.') == -1;

    public isWord: (word?: string) => boolean = (word) => {
        return this.word != null && (word ? this.word == word : true);
    }

    public isLiteral: () => boolean = () => {
        return this.literal != null;
    }

    public isExpression: () => boolean = () => {
        return this.expression != null;
    }

    public isPath: () => boolean = () => {
        return this.path != null;
    }
}


// ------------------------------------------------------------------------------------------------------------------
class Group {

    resource: string;
    tokens: Token[] = [];

    constructor(resource: string) {
        this.resource = resource;
    }

    public token: (token: Token) => void = (token) => {
        this.tokens.push(token);
    }

    public closeAsCondition: () => Token | null = () => {
        if (this.tokens.length < 2) {
            return null;
        }
        const params = this.tokens.slice(2).map(token => token.isWord() ? Token.pathToken({ path: token.word! }) : token);

        if (params.length > 0 && params.find(t => !(t.isExpression() || t.isLiteral())) != null) {
            return null;
        }

        const lhsToken = this.tokens[0];
        let lhs: ExpressionPath;

        if (lhsToken.isPath()) {
            lhs = lhsToken.path!;

        } else if (lhsToken.isWord()) {
            lhs = this.parseWord(lhsToken.word!);

        } else {
            return null;
        }

        const opToken = this.tokens[1];
        if (!opToken.isWord()) {
            return null;
        }

        const ops: ConditionOp[] = getOps(lhs);
        const op: ConditionOp | undefined = ops.find(o => o.name == opToken.word);

        if (op == null || op.arity != params.length) {
            return null;
        }
        const rhs = this.parseParamTokens(params);

        const expression: Expression = getNewExpression();
        expression.lhs = { path: lhs };
        expression.op = op;
        expression.rhs = rhs;

        return Token.expressionToken(expression);
    }

    public closeAsArgs: () => ExpressionPath[] = () => {
        return this.parseParamTokens(this.tokens);
    }

    public invoke: (closing: Group) => void = (closing) => {
        if (this.tokens.length == 0 || this.topToken().isSimpleWord()) {
            const expToken = closing.closeAsCondition();
            if (expToken == null) {
                throw new Error('Cannot close condition');
            }
            const negate = this.tokens.length > 0 && this.topToken().isWord('not');
            if (negate) {
                expToken.expression!.negated = true;
                this.tokens.pop();
            }
            if (this.tokens.length > 0) {
                const logOpToken = this.tokens.pop();
                if (!logOpToken!.isSimpleWord()) {
                    throw new Error('Cannot parse filter');
                }
                const logOp: LogOp | null = logOpToken!.word == 'or' ? LogOp.or : logOpToken!.word == 'and' ? LogOp.and : null;
                if (logOp == null) {
                    throw new Error('Cannot parse filter: logical operation expected, instead got: ' + logOpToken!.word);
                }
                if (!this.topToken().isExpression()) {
                    throw new Error('Cannot parse filter: logical expression expected.');
                }
                const prevExp: Expression = this.topToken().expression!;
                pushItem(prevExp, expToken.expression!, logOp);
            } else {
                this.tokens.push(expToken);
            }
        } else if (this.topToken().isWord() && !this.topToken().isSimpleWord()) {
            const word = this.tokens.pop()!.word!;
            const args = closing.closeAsArgs();

            if (word.charAt(0) != '.') {
                const path = this.parseWordAsFunc(word, args);
                this.tokens.push(Token.pathToken(path));
            } else {
                const topToken = this.tokens.pop()!;
                if (!topToken.isPath()) {
                    throw new Error('Cannot parse filter.')
                }
                const path: ExpressionPath = topToken.path!;
                const funcName = word.substring(1);
                const pathType: PathType = path.func!.spec.returns;
                const funcSpecs: ExpressionFunctionSpec[] = specsToTypes[pathType];
                const spec: ExpressionFunctionSpec | undefined = funcSpecs.find(spec => spec.name == funcName);
                if (spec == null) {
                    throw new Error('Cannot parse filter.')
                }
                this.tokens.push(Token.pathToken({
                    path: topToken.path!,
                    func: {
                        spec,
                        args,
                    }
                }));
            }
        } else {
            throw new Error('Cannot parse filter.');
        }
    }

    private parseWordAsFunc: (word: string, args: ExpressionPath[]) => ExpressionPath = (word, args) => {
        const path = this.parseWord(word.substring(0, word.lastIndexOf('.')));
        const funcName = word.substring(word.lastIndexOf('.') + 1);
        const spec: ExpressionFunctionSpec | undefined = this.parseFunc(path.field!, funcName);
        if (spec == null) {
            throw new Error('Function not found: ' + funcName);
        }
        path.func = {
            spec,
            args,
        }
        return path;
    }

    private parseFunc: (field: Field, funcName: string) => ExpressionFunctionSpec | undefined = (field, funcName) => {
        const type: PathType = typesMap[field.type];
        const funcSpecs: ExpressionFunctionSpec[] = specsToTypes[type];
        const spec: ExpressionFunctionSpec | undefined = funcSpecs.find(spec => spec.name == funcName);
        return spec;
    }

    private parseWord: (word: string) => ExpressionPath = (word) => {
        let resource = conf.getResource(this.resource);
        let availAssoc = getAvailAssoc(resource.name, true);

        const parts = word.split('.');
        parts.slice(0, parts.length - 1).forEach(attr => {
            resource = availAssoc[attr];
            if (resource == null) {
                throw new Error('cannot parse path: "' + word + '" attribute (resource) not found: "' + attr + '"');
            }
            availAssoc = getAvailAssoc(resource.name, true);
        });

        const field: Field | undefined = conf.getField(resource.name, _.last(parts)!);

        if (field == null) {
            throw new Error('cannot parse path: "' + word + '" attribute (field) not found: "' + _.last(parts)! + '"');
        }

        return { path: word, field };
    }

    private parseParamTokens: (params: Token[]) => ExpressionPath[] = (params) => {
        const paths: ExpressionPath[] = [];
        for (const param of params) {
            if (param.isWord()) {
                paths.push(this.parseWord(param.word!));
            } else if (param.isLiteral()) {
                paths.push({ path: { value: param.literal! } });
            } else if (param.isPath()) {
                paths.push(param.path!);
            }
        }
        return paths;
    }

    public topToken: () => Token = () => {
        return this.tokens[this.tokens.length - 1];
    }
}


// ------------------------------------------------------------------------------------------------------------------
export class Parser {

    resource: string;
    expression: Expression;

    groups: Group[] = [];
    lit: string[] = [];
    word: string[] = [];
    litOpen: boolean = false;
    whiteSpaceOpen: boolean = false;

    constructor(resource: string) {
        this.resource = resource;
        this.expression = getNewExpression();
    }

    public parse: (filter: string) => Expression = (filter) => {
        const tokens = this.tokenize(filter);
        this.litOpen = false;

        tokens.reduce((acc, symbol) => {
            if ("\"" == symbol) {
                this.quote();
            } else if (this.litOpen) {
                this.literal(symbol);
            } else if (!hasText(symbol)) {
                this.whiteSpace();
            } else if ("(" == symbol) {
                this.groupOpen();
            } else if (")" == symbol) {
                this.groupClose();
            } else {
                this.wordSymbol(symbol);
            }
            return acc;
        }, {});

        return this.expression;
    }

    private quote: () => void = () => {
        this.whiteSpaceOpen = false;
        if (this.litOpen) {
            this.endLiteral();
        }
        this.litOpen = !this.litOpen;
    }

    private literal: (char: string) => void = (char) => {
        this.lit.push(char == "\\\\" ? "\\" : char);
    }

    private endLiteral: () => void = () => {
        this.topGroup().token(Token.literalToken(this.lit.join('')));
        this.lit = [];
    }

    private whiteSpace: () => void = () => {
        if (!this.whiteSpaceOpen) {
            this.whiteSpaceOpen = true;
            this.endWord();
        }
    }

    private groupOpen: () => void = () => {
        this.whiteSpaceOpen = false;
        this.endWord();
        this.groups.push(new Group(this.resource));
    }

    private groupClose: () => void = () => {
        this.whiteSpaceOpen = false;
        this.endWord();
        const closing = this.groups.pop()!;

        if (this.groups.length > 0) {
            this.topGroup().invoke(closing!);

        } else if (closing.tokens.length > 1) {
            const expToken: Token | null = closing!.closeAsCondition();
            if (expToken == null) {
                throw new Error('Cannot close condition');
            }
            this.expression = expToken.expression!;

        } else if (closing.tokens.length == 1) {
            const expToken: Token = closing.topToken();
            if (!expToken.isExpression()) {
                throw new Error('Cannot parse filter');
            }
            this.expression = expToken.expression!;
        }
    }

    private wordSymbol: (char: string) => void = (char) => {
        this.whiteSpaceOpen = false;
        this.word.push(char);
    }

    private endWord: () => void = () => {
        if (this.word.length > 0) {
            this.topGroup().token(Token.wordToken(this.word.join('')));
            this.word = [];
        }
    }

    private tokenize: (str: string) => string[] = (str) => {
        let totalCount = 0;
        const tokens: string[] = [];
        let remainder = str;
        while (remainder.length > 0 && totalCount < 100) {
            const pat = '\\\\(.)';
            const re = new RegExp(pat, 'g');
            totalCount++;
            const reArr = re.exec(remainder);
            if (reArr != null) {
                const fragment = remainder.substring(0, reArr.index);
                fragment.split('').reduce((acc, char) => {
                    acc.push(char);
                    return acc;
                }, tokens);
                remainder = remainder.substring(reArr.index + reArr[0].length);
                tokens.push(reArr[0]);
            } else {
                remainder.split('').reduce((acc, char) => {
                    acc.push(char);
                    return acc;
                }, tokens);
                remainder = '';
            }
        }
        return tokens;
    }

    private topGroup: () => Group = () => {
        return this.groups[this.groups.length - 1];
    }
}
